import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Edit, MapPin, Calendar, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePosts } from '../contexts/PostContext';
import PostCard from '../components/posts/PostCard';
import EditProfileModal from '../components/profile/EditProfileModal';
import FollowListModal from '../components/profile/FollowListModal';
import { supabase } from '../lib/supabase';
import { formatDistanceToNow } from 'date-fns';

interface ProfileData {
  id: string;
  username: string;
  photoURL: string;
  bio: string;
  email: string;
  location: string;
  followerCount: number;
  followingCount: number;
  isFollowedByUser: boolean;
  is_verified?: boolean;
  created_at?: string;
}

const Profile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { currentUser, followUser, unfollowUser } = useAuth();
  const { posts, getUserPosts } = usePosts();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [followListType, setFollowListType] = useState<'followers' | 'following' | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  
  const targetUsername = username;
  const isCurrentUser = currentUser?.username.toLowerCase() === targetUsername?.toLowerCase();
  
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!targetUsername) {
        console.error('❌ No username provided to Profile component');
        setError('No username provided');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch basic profile data with case-insensitive username comparison
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .ilike('username', targetUsername)
          .maybeSingle();

        if (profileError) {
          throw profileError;
        }
        
        if (!profile) {
          setProfileData(null);
          setIsLoading(false);
          return;
        }

        // Fetch follower count
        const { count: followerCount, error: followerError } = await supabase
          .from('follows')
          .select('*', { count: 'exact', head: true })
          .eq('following_id', profile.id);

        if (followerError) {
          // Don't throw, just use 0
        }

        // Get the following count
        const { count: followingCount, error: followingError } = await supabase
          .from('follows')
          .select('*', { count: 'exact', head: true })
          .eq('follower_id', profile.id);

        if (followingError) {
          // Don't throw, just use 0
        }

        // Check if the current user follows this profile
        let isFollowedByUser = false;
        if (currentUser && currentUser.id !== profile.id) {
          const { data: followData, error: followError } = await supabase
            .from('follows')
            .select('follower_id')
            .eq('follower_id', currentUser.id)
            .eq('following_id', profile.id)
            .maybeSingle();

          if (followError) {
            // Don't throw, just use false
          } else {
            isFollowedByUser = !!followData;
          }
        }

        const profileData: ProfileData = {
          id: profile.id,
          username: profile.username,
          photoURL: profile.photo_url || 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
          bio: profile.bio || '',
          email: profile.email || '',
          location: profile.location || '',
          followerCount: followerCount || 0,
          followingCount: followingCount || 0,
          isFollowedByUser,
          is_verified: profile.is_verified || false,
          created_at: profile.created_at
        };

        setProfileData(profileData);
      } catch (error) {
        setError('Failed to load profile');
        setProfileData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [targetUsername, currentUser]);
  
  const handleFollowToggle = async () => {
    if (!profileData || !currentUser || isFollowLoading) return;
    
    setIsFollowLoading(true);
    try {
      if (profileData.isFollowedByUser) {
        await unfollowUser(profileData.id);
      } else {
        await followUser(profileData.id);
      }
      
      // Update local state
      setProfileData(prev => prev ? {
        ...prev,
        isFollowedByUser: !prev.isFollowedByUser,
        followerCount: prev.isFollowedByUser 
          ? prev.followerCount - 1 
          : prev.followerCount + 1
      } : null);
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };
  
  const userPosts = isCurrentUser 
    ? getUserPosts(currentUser?.id || '') 
    : posts.filter(post => post.username.toLowerCase() === targetUsername?.toLowerCase());
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-2xl font-bold text-white mb-4">Error Loading Profile</h2>
        <p className="text-gray-400 mb-6">{error}</p>
        <Link 
          to="/home"
          className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
        >
          Back to Home
        </Link>
      </div>
    );
  }
  
  if (!profileData) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-2xl font-bold text-white mb-4">User not found</h2>
        <p className="text-gray-400 mb-6">The user "{targetUsername}" doesn't exist.</p>
        <Link 
          to="/home"
          className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
        >
          Back to Home
        </Link>
      </div>
    );
  }
  
  return (
    <>
      {/* Profile Header - Aligned with sidebar */}
      <div className="mb-8">
        {/* Profile Picture and Info */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-6">
          <div className="relative mb-4 sm:mb-0">
            <img 
              src={profileData.photoURL} 
              alt={profileData.username}
              className="w-24 h-24 rounded-full object-cover border-2 border-gray-700"
            />
            {profileData.is_verified && (
              <div className="absolute -bottom-1 -right-1 bg-purple-500 rounded-full p-1">
                <Shield className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
          
          {/* Profile Info */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">
                    {profileData.username}
                  </h1>
                  {profileData.is_verified && (
                    <Shield className="w-6 h-6 text-purple-400" />
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-4 mt-2 text-gray-400 text-sm">
                  {profileData.location && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span>{profileData.location}</span>
                    </div>
                  )}
                  {profileData.created_at && (
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>Joined {formatDistanceToNow(new Date(profileData.created_at), { addSuffix: true })}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Button */}
              <div className="mt-4 sm:mt-0">
                {isCurrentUser ? (
                  <button 
                    onClick={() => setIsEditModalOpen(true)}
                    className="px-6 py-2 bg-[#272727] text-white rounded-lg hover:bg-[#404040] transition-colors flex items-center space-x-2 border border-gray-600"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit Profile</span>
                  </button>
                ) : (
                  <button 
                    onClick={handleFollowToggle}
                    disabled={isFollowLoading}
                    className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      profileData.isFollowedByUser
                        ? 'bg-[#272727] text-white border border-gray-600 hover:bg-[#404040]' 
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                  >
                    {isFollowLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <span>{profileData.isFollowedByUser ? 'Following' : 'Follow'}</span>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {/* Bio */}
            {profileData.bio && (
              <p className="text-gray-300 mt-4 leading-relaxed">
                {profileData.bio}
              </p>
            )}
            
            {/* Stats */}
            <div className="flex items-center space-x-8 mt-6">
              <div className="text-center">
                <div className="text-xl font-bold text-white">{userPosts.length}</div>
                <div className="text-sm text-gray-400">Posts</div>
              </div>
              <button 
                onClick={() => setFollowListType('followers')}
                className="text-center hover:opacity-80 transition-opacity"
              >
                <div className="text-xl font-bold text-white">{profileData.followerCount}</div>
                <div className="text-sm text-gray-400">Followers</div>
              </button>
              <button
                onClick={() => setFollowListType('following')}
                className="text-center hover:opacity-80 transition-opacity"
              >
                <div className="text-xl font-bold text-white">{profileData.followingCount}</div>
                <div className="text-sm text-gray-400">Following</div>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Posts Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Posts</h2>
          {isCurrentUser && userPosts.length === 0 && (
            <Link 
              to="/home/create"
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
            >
              Create your first post
            </Link>
          )}
        </div>
        
        {userPosts.length === 0 ? (
          <div className="bg-[#272727] rounded-xl p-12 text-center">
            <div className="text-gray-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              {isCurrentUser ? "You haven't posted anything yet" : "No posts yet"}
            </h3>
            <p className="text-gray-500 mb-6">
              {isCurrentUser 
                ? "Share your first story with the community" 
                : `${profileData.username} hasn't shared any stories yet`
              }
            </p>
            {isCurrentUser && (
              <Link 
                to="/home/create"
                className="inline-flex items-center px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                <Edit className="w-4 h-4 mr-2" />
                Write your first post
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {userPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <EditProfileModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />

      {followListType && profileData && (
        <FollowListModal
          isOpen={true}
          onClose={() => setFollowListType(null)}
          userId={profileData.id}
          type={followListType}
          title={followListType === 'followers' ? 'Followers' : 'Following'}
        />
      )}
    </>
  );
};

export default Profile;